import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { matchSwapDirection } from '@root/entities/trade-type/model/trade-type'
import { TradeSettingsPresetSelector } from '@root/features/trade/trade-settings/ui/common/trade-settings-preset-selector'
import {
  tradeSwapTypeAtom,
  tradingPairAtom,
} from '@root/features/trade/trading'

import { TradingHeadingTabs } from '../common/trading-heading-tabs'
import { TradeSettingsType } from '../common/trading-settings-type'

import { TradingControlsBuyForm } from './trading-controls-buy/trading-controls-buy-form'
import { TradingControlsSellForm } from './trading-controls-sell/trading-controls-sell-form'
import { TradingControlsSkeleton } from './trading-controls-skeleton'
import { TradingControlsWallet } from './trading-controls-wallet'

import type { BuyControlsModel, SellControlsModel } from '../../atoms/common'

type Props = {
  onSettingsOpen: () => void
  buyControlsModel: BuyControlsModel
  sellControlsModel: SellControlsModel
}

export const TradingControls: React.FC<Props> = ({
  onSettingsOpen,
  buyControlsModel,
  sellControlsModel,
}) => {
  const { t } = useTranslation()
  const [activePair] = useAtom(tradingPairAtom)
  const [tradeSwapType, setTradeSwapType] = useAtom(tradeSwapTypeAtom)

  const renderContent = () => {
    if (!activePair) {
      return <TradingControlsSkeleton faded={false} />
    }

    return (
      <Container>
        <TypeAndPresetContainer>
          <TradeSettingsType
            swapDirection={tradeSwapType}
            onChange={(type) => setTradeSwapType(type)}
            buyLabel={t('trading.buy')}
            sellLabel={t('trading.sell')}
          />
          <Separator />

          <PresetSelector size="h30" />
        </TypeAndPresetContainer>
        <TradingHeadingTabs>
          <TradingControlsWallet pair={activePair} />
        </TradingHeadingTabs>
        {matchSwapDirection(tradeSwapType, {
          onBuy: () => (
            <TradingControlsBuyForm
              model={buyControlsModel}
              onSettingsOpen={onSettingsOpen}
              pair={activePair}
            />
          ),
          onSell: () => (
            <TradingControlsSellForm
              model={sellControlsModel}
              onSettingsOpen={onSettingsOpen}
              pair={activePair}
            />
          ),
        })}
      </Container>
    )
  }

  return <Root>{renderContent()}</Root>
}

const Root = styled.div`
  position: relative;

  overflow: hidden;
`

const Container = styled.div`
  background: var(--background-2);
  border: 1px solid var(--backgroundA-3);

  border-radius: 12px;
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 10px 0;
`

const TypeAndPresetContainer = styled.div`
  display: grid;
  gap: 8px;
  padding-inline: 10px;
  margin-bottom: 2px;
`

const Separator = styled.div`
  height: 1px;

  border-radius: 2px;
  background: #262626;
  background: var(--background-4);
`

const PresetSelector = styled(TradeSettingsPresetSelector)`
  justify-self: start;
`
